/*
 * Jira Ticket:
 * Created Date: Thu, 12th Mar 2020, 14:25:08 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */
import React, { useCallback } from 'react';
import {
  ArrayField,
  ChipField,
  Datagrid,
  NumberField,
  SelectField,
  SingleFieldList,
  TextField,
  List,
  Title,
  useGetList,
  useRedirect,
  useTranslate,
} from 'react-admin';
import { EmptyResource } from 'Layout';
import SubcontractorFilters from './filter';
import SubcontractorActions from './actions';
import { subcontractorStatusOptions } from 'utils/choices';
import { PostPagination } from '../../Components/CustomPagination/pagination';
import { usePersistentOrganisation } from '../../Components/Contexts';

const SubcontractorList = props => {
  const redirect = useRedirect();
  const translate = useTranslate();
  const {
    persistentOrganisation,
    onSelectPersistentOrganisation,
  } = usePersistentOrganisation();

  const { total, loading, error } = useGetList('Organisation', {
    page: 1,
    perPage: 1,
  });
  const hasOrganisations = !loading && !error && total > 0;

  const EmptySubcontractorsCallback = useCallback(() => {
    const EmptySubcontractors = () => {
      const title = hasOrganisations
        ? 'resources.Subcontractor.emptyStateTitle'
        : 'resources.Subcontractor.emptyStateNoOrgTitle';
      const subtitle = hasOrganisations
        ? 'resources.Subcontractor.inviteSubcontractor'
        : 'resources.Subcontractor.emptyStateNoOrgSubtitle';
      const buttonLabel = hasOrganisations
        ? 'inviteSubcontractor'
        : 'createOrganisation';
      const basePath = hasOrganisations ? props.basePath : '/Organisation';
      return (
        <EmptyResource
          title={title}
          subtitle={subtitle}
          buttonLabel={buttonLabel}
          basePath={basePath}
        />
      );
    };
    return EmptySubcontractors();
  }, [hasOrganisations, redirect]);

  return (
    <>
      <Title title={`resources.General.cmsTitle`} />
      {hasOrganisations ? (
        <List
          filterDefaultValues={
            persistentOrganisation.id
              ? {
                  organisationId: persistentOrganisation.id,
                }
              : {
                  isSubcontractors: true,
                }
          }
          empty={<EmptySubcontractorsCallback />}
          title={translate('resources.Subcontractor.name', { smart_count: 2 })}
          bulkActionButtons={false}
          perPage={25}
          pagination={<PostPagination />}
          actions={
            <SubcontractorActions
              exporter={null}
              organisationId={persistentOrganisation.id}
            />
          }
          filters={
            <SubcontractorFilters
              organisationId={persistentOrganisation.id}
              organisationFilter={onSelectPersistentOrganisation}
            />
          }
          {...props}
        >
          <Datagrid
            rowClick={(id, basePath, record) => {
              return {
                pathname: `/Subcontractor/${record.id}`,
                state: {
                  organisationId:
                    persistentOrganisation.id ?? record.organisation.id,
                },
              };
            }}
          >
            <TextField
              source="subcontractor.name"
              label={translate('resources.Subcontractor.name', {
                smart_count: 1,
              })}
              sortable={false}
            />
            <TextField
              source="organisation.name"
              label={'resources.Subcontractor.fields.workingFor'}
              sortable={false}
            />
            <ArrayField
              source="projects"
              sortable={false}
              label={'resources.Subcontractor.fields.assignedProjects'}
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" />
              </SingleFieldList>
            </ArrayField>
            <NumberField
              source="statistics.numberOfUsers"
              label={'resources.Subcontractor.fields.numberOfUsers'}
              sortable={false}
            />
            <SelectField
              source="status"
              choices={subcontractorStatusOptions}
              label={'resources.Subcontractor.fields.status'}
              sortable={false}
            />
          </Datagrid>
        </List>
      ) : (
        EmptySubcontractorsCallback()
      )}
    </>
  );
};

export default SubcontractorList;
